import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { CATEGORIES, getRelated } from "@/data/calculators";
import { ARTICLES } from "@/data/articles";
import CalculatorCard from "@/components/CalculatorCard";
import Seo from "@/components/Seo";
import ResultActions from "@/components/ResultActions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// ------------------------------------------------------------------
// Turns [[Link Text|some-calculator-slug]] inside article text into a
// real clickable link to /calculators/some-calculator-slug.
// Everything else is left as plain text.
// ------------------------------------------------------------------
function renderWithLinks(text) {
  if (!text) return text;
  const regex = /\[\[(.+?)\|(.+?)\]\]/g;
  const result = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(
      <Link
        key={key++}
        to={`/calculators/${match[2]}`}
        className="text-primary underline underline-offset-2 hover:no-underline"
      >
        {match[1]}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result;
}

// ------------------------------------------------------------------
// Renders one block of an article's "sections" array.
// Supported block types: "h2", "h3", "h4", "p" (default).
// ------------------------------------------------------------------
function renderArticleBlock(block, i) {
  const content = renderWithLinks(block.text);
  switch (block.type) {
    case "h2":
      return (
        <h2 key={i} className="text-lg sm:text-xl font-bold text-foreground mt-6 mb-2 first:mt-0">
          {content}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="text-base sm:text-lg font-semibold text-foreground mt-5 mb-2 first:mt-0">
          {content}
        </h3>
      );
    case "h4":
      return (
        <h4 key={i} className="text-sm sm:text-base font-semibold text-foreground mt-4 mb-1 first:mt-0">
          {content}
        </h4>
      );
    default:
      return <p key={i}>{content}</p>;
  }
}

// Universal wrapper for every calculator page. Handles breadcrumbs, SEO, disclaimer,
// FAQ section (with FAQPage schema), and "Related Calculators".
export default function CalculatorLayout({
  calc,
  intro,
  formula,
  faqs = [],
  resultText,
  children,
}) {
  const category = CATEGORIES.find((c) => c.id === calc.category);
  const related = getRelated(calc.slug);
  const article = ARTICLES[calc.slug];

  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: calc.name,
    applicationCategory: "HealthApplication",
    operatingSystem: "Any",
    description: calc.description,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
      { "@type": "ListItem", position: 2, name: category?.label, item: window.location.origin + `/?cat=${calc.category}` },
      { "@type": "ListItem", position: 3, name: calc.name, item: window.location.href },
    ],
  };

  const schemas = [appSchema, breadcrumbSchema];
  if (faqSchema) schemas.push(faqSchema);

  return (
    <>
      <Seo
        title={`${calc.name} — Free & Instant | Fitness Calculator`}
        description={`${calc.description} Free ${calc.name.toLowerCase()} with instant results, formula explanation and FAQs.`}
        path={`/calculators/${calc.slug}`}
        schemas={schemas}
      />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12">
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          data-testid="breadcrumbs"
          className="flex items-center gap-1 text-xs text-muted-foreground mb-6 flex-wrap"
        >
          <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground">
            <Home className="h-3 w-3" /> Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/?cat=${calc.category}`} className="hover:text-foreground">
            {category?.label}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{calc.name}</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{calc.name}</h1>
          {intro && (
            <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl">
              {intro}
            </p>
          )}
        </header>

        {calc.disclaimer && (
          <div
            role="note"
            data-testid="medical-disclaimer"
            className="mb-8 rounded-xl border border-border bg-secondary/50 p-4 text-sm text-muted-foreground"
          >
            <strong className="text-foreground">Note:</strong> For informational purposes only — not medical advice. Please consult a qualified healthcare professional.
          </div>
        )}

        <section
          data-testid="calculator-section"
          className="rounded-2xl border border-border bg-card p-5 sm:p-8"
        >
          {children}
          {resultText && (
            <div className="mt-6 pt-6 border-t border-border">
              <ResultActions text={resultText} title={calc.name} />
            </div>
          )}
        </section>

        {formula && (
          <section className="mt-10">
            <h2 className="text-xl sm:text-2xl font-bold mb-3">How it's calculated</h2>
            <div className="prose-sm max-w-none text-sm sm:text-base text-muted-foreground leading-relaxed space-y-3">
              {formula}
            </div>
          </section>
        )}

        {faqs.length > 0 && (
          <section className="mt-12" data-testid="faq-section">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {article && (
          <section className="mt-12" data-testid="seo-article">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">{article.heading}</h2>
            <div className="prose-sm max-w-none text-sm sm:text-base text-muted-foreground leading-relaxed space-y-4">
              {article.sections
                ? article.sections.map((block, i) => renderArticleBlock(block, i))
                : article.paragraphs.map((p, i) => (
                    <p key={i}>{renderWithLinks(p)}</p>
                  ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Related calculators</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <CalculatorCard key={r.slug} calc={r} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
